import { useMemo } from "react";
import type { FormValues } from "@/widgets/classic-form/form";
import { useFormAPI } from "@/widgets/classic-form/form";
import { FieldsGrid } from "@/features/fields-grid";
import { publishAddField, publishRemoveField } from "@/features/form-fields";
import type { Event } from "@/entities/data-model";
import { getEventModelName } from "@/entities/data-model";
import type { EventFieldsGroup, EventFieldset } from "@/entities/form-metadata";
import { EventAssertion, FieldType } from "@/entities/form-metadata";
import type { FieldPath } from "@/shared/forms";
import { FieldMapper } from "./FieldMapper/FieldMapper";
import { AddNewEventFieldPanel } from "./Panel/AddNewEventFieldPanel";
import { YearRangeEventGroup } from "./YearRangeEventGroup";
import { EVENT_GRID_LAYOUT, YEAR_RANGE_GRID_LAYOUT } from "./gridLayout";

interface EventGroupFormFieldsProps {
    fieldGroup: EventFieldsGroup;
}

export const EventGroup: React.FC<EventGroupFormFieldsProps> = ({ fieldGroup }) => {
    const { fieldsets, assertions, fieldsetDisplayStrategy } = fieldGroup;
    const removable = fieldsetDisplayStrategy !== "prefilled-static";
    const { getFieldValue } = useFormAPI();

    const onRemoveItem = publishRemoveField;

    const addNew = publishAddField;

    const dataAccessorFn = (fieldset: EventFieldset, index: number) =>
        `events.${getEventModelName(fieldset)}.instances.${index}` as FieldPath<FormValues>;

    const formArrayAccessorFn = (fieldset: EventFieldset) =>
        `events.${getEventModelName(fieldset)}.instances` as FieldPath<FormValues>;

    const handleItemAdded = (fieldset: EventFieldset) => {
        const priorityAssertion = assertions.find(({ assertion }) =>
            fieldset.fields.some((field) => field.assertion === assertion),
        )?.assertion;
        const assertionMap = {
            [EventAssertion.Day]: "date.day",
            [EventAssertion.Month]: "date.month",
            [EventAssertion.Year]: "date.year",
            [EventAssertion.Place]: "location",
        };

        const mappedAssertion = priorityAssertion && assertionMap[priorityAssertion];
        if (mappedAssertion) {
            const eventKey = getEventModelName(fieldset);
            const instancesPath = `events.${eventKey}.instances` as FieldPath<FormValues>;
            const instances = (getFieldValue(instancesPath) as unknown as Array<Event>) || [];
            const newItemIndex = instances.length;
            addNew({
                fieldName: instancesPath,
                type: "event",
                newItemIndex,
                mappedAssertion,
            });
        }
    };

    const hasYearRange = useMemo(() => {
        for (const fieldset of fieldsets) {
            for (const field of fieldset.fields) {
                if (field.fieldType === FieldType.YearRange) {
                    return true;
                }
            }
        }
        return false;
    }, [fieldsets]);

    // When at least single field with YearRange found
    // Render a different layout altogether
    if (hasYearRange) {
        return (
            <YearRangeEventGroup
                fieldGroup={fieldGroup}
                FieldMapper={FieldMapper}
                gridConfig={{ Assertions: YEAR_RANGE_GRID_LAYOUT.assertions, Removable: removable }}
            />
        );
    }

    const controlsRow = <AddNewEventFieldPanel fieldsets={fieldsets} onItemAdded={handleItemAdded} />;

    return (
        <FieldsGrid<EventAssertion, EventFieldset, EventFieldsGroup>
            fieldGroup={fieldGroup}
            fieldSetToFormArrayPathFn={formArrayAccessorFn}
            fieldDataToFormFieldPathFn={dataAccessorFn}
            FieldMapper={FieldMapper}
            controlsRow={controlsRow}
            onRemoveItem={onRemoveItem}
            gridConfig={{
                FieldSetName: EVENT_GRID_LAYOUT.fieldsetLabel,
                Assertions: EVENT_GRID_LAYOUT.assertions,
                Removable: removable,
            }}
        />
    );
};
